// CustomCursorNewBadgeSeen — カーソル入口 (Classic 上部バー「カーソル」ボタン /
// Modern 左ツール列のカーソルボタン) の「New!」バッジを「初回だけ」出すための
// seen フラグ永続化 (infrastructure 層、custom-cursor-2026-09-19.md フォローアップ)。
//
// 背景 (作者要望 2026-09-19「カーソルの入口ボタンに New! バッジ」):
//   - 既存の「New!」バッジ機構 (取り消し = `undoBadgeSeen.ts`、過去キャンバス /
//     絵ログ共有 = `pastCanvasSeen.ts` / `timelapseShareSeen.ts`) と**そっくり複製**し、
//     別 localStorage key で独立管理する。
//   - 消える条件は「どの入口からでもエディタを一度開いたら seen」。Classic / Modern
//     両方が同じ store (= 同じ localStorage key) を参照するので、どちらのレイアウトの
//     どの入口 (上部バー / ツール列 / 参加者リスト自分の行) からエディタを開いても
//     両方のバッジが消える。
//   - 参加者リストの自分の行のプレースホルダには付けない (= 既に
//     `customCursorHint.ts` 発の初回 pulse があるため、別の演出と役割分担する)。
//
// 設計 (`undoBadgeSeen.ts` / `pastCanvasSeen.ts` と同流儀):
//   - boolean を文字列 `'1'` で保存 (= 在れば seen 済)。不在 / storage 不可は「未 seen」
//     (= まだ出す) 扱い。例外を throw しない。
//   - StorageLike 抽象 + sentinel probe。test で storage mock 注入可能。
//
// Tests: customCursorNewBadgeSeen.test.ts (storage-injection style)。

/** localStorage キー: カーソル入口の New! バッジを消したか (= 初回にエディタを開いたか). */
export const CUSTOM_CURSOR_NEW_BADGE_SEEN_KEY = 'oekaki:custom-cursor-new-seen';

/** Minimal Storage shape this module uses (pastCanvasSeen.ts と同流儀). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CustomCursorNewBadgeSeenStore {
  /** 既に New! バッジを消したか (= 初回にエディタを開いたか). 不在 / storage 不可 → false. */
  hasSeen(): boolean;
  /** 初回にエディタを開いたことを記録 (= 以後バッジ非表示). storage 不可なら no-op. */
  markSeen(): void;
  /** key を削除. テスト / リセット用. storage 不可なら no-op. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:custom-cursor-new-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateCustomCursorNewBadgeSeenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= hasSeen always false, markSeen silently drops).
   */
  storage?: StorageLike | null;
}

/**
 * `CustomCursorNewBadgeSeenStore` のインスタンスを構築. storage 不可でも例外を出さず、
 * 「未 seen」扱い (= 出すが永続化されないだけ) で動作を継続する.
 */
export const createCustomCursorNewBadgeSeenStore = (
  opts: CreateCustomCursorNewBadgeSeenStoreOptions = {},
): CustomCursorNewBadgeSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(CUSTOM_CURSOR_NEW_BADGE_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(CUSTOM_CURSOR_NEW_BADGE_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(CUSTOM_CURSOR_NEW_BADGE_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
